import { SetStateAction, useEffect, useMemo, useState } from "react"
import { Alert, Select, Tooltip } from "flowbite-react"
import { useQueryClient } from "react-query"
import debounce from "debounce"

import TableItem from "@/components/Table"
import PaginationItem from "@/components/Table/pagination-item"
import { TABLE_HEADERS_REGISTROS_PAGE } from "@/pages/Pedidos/constants"
import { useRegistrosContext } from "@/contexts/registros.context"
import SearchTableItem from "@/components/Table/search"
import RegistrosFilterForm from "@/components/pedidos/devolucoes/registro-filtros.component"
import { ArrowPathIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import { QueryKey } from '@/configs/queryKeys'
import Loader from "@/components/Loader"
import DetalhesImportacaoModal from "@/components/pedidos/devolucoes/detalhes-importacao-modal.component"
import AcoesUploadModal from "@/components/pedidos/devolucoes/acoes-upload.modal"

const RegistrosContainer = () => {
  const [isDetalhesImportacaoModalOpen, setIsDetalhesImportacaoModalOpen] = useState<boolean>(false)
  const [detalhesImportacaoModal, setDetalhesImportacaoModal] = useState<any>(null)
  const queryClient = useQueryClient()

  const {
    isActionsUploadOpen,
    isFetchingRegistros,
    isGetRegistrosPendentesList,
    isLoadingRegistros,
    pageSize,
    registrosList,
    setIsActionsUploadOpen,
    setIsGetRegistrosPendentesList,
    setPage,
    setPageSize,
    setSearch,
    totalItens,
    totalPages,
  } = useRegistrosContext()

  const getSituacaoDaImportacao = (situacoes: string) => {
    switch (situacoes) {
      case '0':
      case '1':
        return 'Em processamento'
      case '2':
        return 'Erro na importação'
      case '3':
        return 'Importação Realizada'
      default:
        return ''
    }
  }

  const handleAbrirHistoricoDevolucao = (item: any) => {
    setIsDetalhesImportacaoModalOpen(true)
    setDetalhesImportacaoModal(item)
  }

  const mapedProperties = useMemo(() => {
    return registrosList?.conteudo?.registros?.map((item) => ({
      ...item,
      situacaoDaImportacao: getSituacaoDaImportacao(item?.situacaoDaImportacao),
      importadoEm: new Date(item.importadoEm).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
      }),
      actions: (
        (getSituacaoDaImportacao(item?.situacaoDaImportacao) === 'Em processamento') ? (
          <button
            type='button'
            className='btn btn-secondary align-center bg-gray-300 text-gray-700 py-2 px-4 rounded w-full'
            disabled
          >
            Aguarde...
          </button>
        ) : (
          <button
            type='button'
            className='btn btn-primary align-center bg-primary-dark text-white py-2 px-4 rounded w-full'
            id='registerButton'
            onClick={() => handleAbrirHistoricoDevolucao(item)}
          >
            Ver
          </button>
        )
      )
    }))
  }, [registrosList])

  const handleSearch = debounce((e: SetStateAction<string>) => {
    setSearch(e)
  }, 500)

  useEffect(() => {
    setIsGetRegistrosPendentesList(true)
    const handleGetRegistrosPendentesList = debounce(() => {
      setIsGetRegistrosPendentesList(false)
    }, 300)
    setTimeout(handleGetRegistrosPendentesList, 30000)
  }, [isFetchingRegistros])

  return (
    <>
      <div className='pb-5'>
        <Alert color='warning' icon={InformationCircleIcon} rounded>
          <span className='font-medium'>
            Clique no botão <strong>Ver</strong> para conferir os detalhes das
            devoluções importadas e baixar a planilha com os erros identificados.
          </span>
        </Alert>
      </div>
      <div className='pb-5 relative'>
        <SearchTableItem
          isAddItem={false}
          isSearchInputEnabled={true}
          filtersEnabled
          setFilterText={e => handleSearch(e)}
        >
          <RegistrosFilterForm />
        </SearchTableItem>
        <div className='absolute right-4 top-4 recarregar'>
          <Tooltip content='Atualizar lista' placement='left'>
            {isGetRegistrosPendentesList ? (
              <ArrowPathIcon data-testid='refresh-icon-disabled' className={'text-gray-400 w-5 h-6 animate-spin'} />
            ) : (
              <ArrowPathIcon
                data-testid='refresh-icon-enabled'
                className={`text-primary-dark w-5 h-6 cursor-pointer`}
                onClick={() => queryClient.invalidateQueries(QueryKey.registrosList)}
              />
            )}
          </Tooltip>
        </div>
      </div>
      {isLoadingRegistros ? (
        <div className='h-96 w-full flex justify-center items-center'>
          <Loader size={'xl'} />
        </div>
      ) : (
        <div className='w-full bg-white rounded-md p-5 overflow-x-hidden [&_th]:whitespace-normal [&_td]:whitespace-normal'>
          <TableItem
            isActions={false}
            isRowClicable={false}
            data={mapedProperties || []}
            headers={TABLE_HEADERS_REGISTROS_PAGE}
            deleteAction={() => void 0}
            hasCustomActions={false}
            idKey='id'
            isIdle={isLoadingRegistros || isFetchingRegistros}
          />
          <PaginationItem
            totalItems={totalItens}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={(pageParam: number) => setPage(pageParam)}
            forceDisplay
          >
            <Select
              id='select-page-size'
              data-testid='select-page-size'
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value='10'>10</option>
              <option value='25'>25</option>
              <option value='50'>50</option>
              <option value='100'>100</option>
            </Select>
          </PaginationItem>
        </div>
      )}
      <DetalhesImportacaoModal
        isOpen={isDetalhesImportacaoModalOpen}
        onClose={() => {
          setIsDetalhesImportacaoModalOpen(false)
          setDetalhesImportacaoModal(null)
        }}
        detalhesImportacao={detalhesImportacaoModal}
      />

      <AcoesUploadModal
        isOpen={isActionsUploadOpen}
        onClose={() => setIsActionsUploadOpen(false)}
      />
    </>
  )
}

export default RegistrosContainer
